import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Play, Pause } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Hai dao động điều hoà cùng tần số (Lớp 11 — Bài 1): x = A·cos(ωt + φ).
// Chỉnh biên độ A và pha ban đầu φ của từng dao động để thấy ĐỘ LỆCH PHA Δφ = φ₂ − φ₁.
// Pha nhập theo radian, chấp nhận biểu thức chứa pi (vd: pi/2, -pi/3, 2*pi/3).
const W = 680, H = 300, OMEGA = 1, MIDY = 150, SCALE = 78
const GX0 = 150, GX1 = 662
const WINDOW = (2 * 2 * Math.PI) / OMEGA   // 2 chu kì

// Phân tích chuỗi pha (radian) — cho phép pi và phép toán cơ bản
function parsePhase(s: string): number | null {
  const t = s.trim().toLowerCase().replace(/π/g, 'pi')
  if (t === '') return 0
  if (!/^[-+*/().\s0-9pi]*$/.test(t)) return null
  try { const v = Function('"use strict"; const pi = Math.PI; return (' + t + ')')(); return Number.isFinite(v) ? v : null } catch { return null }
}
function fmtPi(r: number): string {
  const k = r / Math.PI   // r = k·π
  const table: [number, string][] = [[0, '0'], [1, 'π'], [-1, '−π'], [0.5, 'π/2'], [-0.5, '−π/2'], [1 / 3, 'π/3'], [-1 / 3, '−π/3'], [0.25, 'π/4'], [-0.25, '−π/4'], [2 / 3, '2π/3'], [-2 / 3, '−2π/3'], [0.75, '3π/4'], [-0.75, '−3π/4'], [1 / 6, 'π/6'], [-1 / 6, '−π/6']]
  for (const [v, lbl] of table) if (Math.abs(k - v) < 0.02) return lbl
  return `${r.toFixed(2)} rad`
}

const TwoOscillationsSimulation: React.FC<Props> = ({
  title = 'Hai dao động điều hoà — độ lệch pha',
  description = 'Hai dao động cùng tần số x = A·cos(ωt + φ). Chỉnh biên độ và pha ban đầu để thấy độ lệch pha Δφ = φ₂ − φ₁. Nhập pha theo radian (vd: pi/2).',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const tRef = useRef(0)
  const [a1, setA1] = useState(1), [a2, setA2] = useState(0.8)
  const [p1s, setP1s] = useState('0'), [p2s, setP2s] = useState('pi/2')
  const [running, setRunning] = useState(true)
  const phi1 = parsePhase(p1s), phi2 = parsePhase(p2s)
  const refs = useRef({ a1, a2, phi1, phi2, running })
  refs.current = { a1, a2, phi1: phi1 ?? 0, phi2: phi2 ?? 0, running }
  const firstRef = useRef(false)
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }

  const x1 = (t: number) => refs.current.a1 * Math.cos(OMEGA * t + refs.current.phi1)
  const x2 = (t: number) => refs.current.a2 * Math.cos(OMEGA * t + refs.current.phi2)

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    const t = tRef.current, tc = t % WINDOW
    // VTCB
    ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(0, MIDY); ctx.lineTo(W, MIDY); ctx.stroke()
    // ===== 2 con lắc lò xo (trái) =====
    const yTop = MIDY - SCALE - 30
    // điểm treo (giá đỡ) + gạch chéo
    ctx.fillStyle = '#94a3b8'; ctx.fillRect(26, yTop - 8, 98, 6)
    ctx.strokeStyle = '#cbd5e1'; ctx.lineWidth = 1
    for (let hx = 30; hx < 122; hx += 8) { ctx.beginPath(); ctx.moveTo(hx, yTop - 8); ctx.lineTo(hx - 5, yTop - 2); ctx.stroke() }
    // lò xo từ điểm treo xuống vật
    const spring = (x: number, y2: number) => {
      const n = 9, w = 7, y1 = yTop - 2, len = (y2 - 9) - (y1 + 6)
      ctx.strokeStyle = '#64748b'; ctx.lineWidth = 1.8; ctx.beginPath(); ctx.moveTo(x, y1); ctx.lineTo(x, y1 + 6)
      for (let i = 0; i < n; i++) ctx.lineTo(x + (i % 2 ? w : -w), y1 + 6 + (len * (i + 0.5)) / n)
      ctx.lineTo(x, y2 - 9); ctx.stroke()
    }
    const drawOsc = (ox: number, val: number, color: string, lbl: string) => {
      // QUỸ ĐẠO: chỉ nét đứt (không vẽ nét liền)
      ctx.strokeStyle = '#cbd5e1'; ctx.setLineDash([3, 3]); ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(ox, MIDY - SCALE); ctx.lineTo(ox, MIDY + SCALE + 12); ctx.stroke(); ctx.setLineDash([])
      const y = MIDY - val * SCALE
      spring(ox, y)
      ctx.fillStyle = color; ctx.fillRect(ox - 11, y - 9, 22, 22)
      ctx.fillStyle = '#fff'; ctx.font = 'bold 12px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(lbl, ox, y + 6); ctx.textAlign = 'left'
    }
    drawOsc(50, x1(t), '#2563eb', '1')
    drawOsc(100, x2(t), '#dc2626', '2')
    ctx.fillStyle = '#64748b'; ctx.font = '11px sans-serif'; ctx.textAlign = 'center'; ctx.fillText('2 con lắc lò xo', 75, MIDY + SCALE + 28); ctx.textAlign = 'left'

    // ===== Đồ thị x–t (phải) =====
    const X = (tau: number) => GX0 + (tau / WINDOW) * (GX1 - GX0)
    const Y = (val: number) => MIDY - val * SCALE
    // ô lưới rõ ràng: dọc mỗi nửa chu kì, ngang ±0,5/±1/±1,5
    ctx.strokeStyle = '#e6ebf1'; ctx.lineWidth = 1
    const per = (2 * Math.PI) / OMEGA
    for (let tau = per / 2; tau < WINDOW - 0.001; tau += per / 2) { ctx.beginPath(); ctx.moveTo(X(tau), 18); ctx.lineTo(X(tau), H - 22); ctx.stroke() }
    for (const lv of [1.5, 1, 0.5, -0.5, -1, -1.5]) { const gy = Y(lv); if (gy > 18 && gy < H - 22) { ctx.beginPath(); ctx.moveTo(GX0, gy); ctx.lineTo(GX1, gy); ctx.stroke() } }
    // trục
    ctx.strokeStyle = '#1e293b'; ctx.fillStyle = '#1e293b'; ctx.lineWidth = 1.8; ctx.lineJoin = 'round'
    ctx.beginPath(); ctx.moveTo(GX0, 18); ctx.lineTo(GX0, H - 22); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(GX0, MIDY); ctx.lineTo(GX1, MIDY); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(GX0, 12); ctx.lineTo(GX0 - 4, 20); ctx.lineTo(GX0 + 4, 20); ctx.closePath(); ctx.fill()
    ctx.beginPath(); ctx.moveTo(GX1 + 6, MIDY); ctx.lineTo(GX1 - 3, MIDY - 4); ctx.lineTo(GX1 - 3, MIDY + 4); ctx.closePath(); ctx.fill()
    ctx.font = 'bold 12px sans-serif'; ctx.fillText('x', GX0 + 4, 16); ctx.fillText('t', GX1 - 2, MIDY - 6)
    // 2 đường cong — CHỈ vẽ phần đã chạy qua (phía SAU điểm chạy), không hiện hình sin phía trước
    const STEPS = 240
    const iMax = Math.ceil((tc / WINDOW) * STEPS)
    const curve = (f: (t: number) => number, color: string) => {
      ctx.strokeStyle = color; ctx.lineWidth = 2.4; ctx.beginPath()
      for (let i = 0; i <= iMax; i++) {
        const tau = Math.min(tc, (i / STEPS) * WINDOW)
        const px = X(tau), py = Y(f(tau))
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)
      }
      ctx.stroke()
    }
    curve((tau) => refs.current.a1 * Math.cos(OMEGA * tau + refs.current.phi1), '#2563eb')
    curve((tau) => refs.current.a2 * Math.cos(OMEGA * tau + refs.current.phi2), '#dc2626')
    // con trỏ thời gian + điểm trên 2 đường
    ctx.strokeStyle = 'rgba(100,116,139,0.5)'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(X(tc), 18); ctx.lineTo(X(tc), H - 22); ctx.stroke()
    const v1 = refs.current.a1 * Math.cos(OMEGA * tc + refs.current.phi1), v2 = refs.current.a2 * Math.cos(OMEGA * tc + refs.current.phi2)
    ctx.fillStyle = '#2563eb'; ctx.beginPath(); ctx.arc(X(tc), Y(v1), 4.5, 0, Math.PI * 2); ctx.fill()
    ctx.fillStyle = '#dc2626'; ctx.beginPath(); ctx.arc(X(tc), Y(v2), 4.5, 0, Math.PI * 2); ctx.fill()
    // chú thích
    ctx.font = 'bold 11px sans-serif'; ctx.fillStyle = '#2563eb'; ctx.fillText('■ x₁', GX1 - 96, 16); ctx.fillStyle = '#dc2626'; ctx.fillText('■ x₂', GX1 - 50, 16)
  }, [])

  const tick = useCallback(() => {
    if (refs.current.running) tRef.current += 0.025
    draw(); raf.current = requestAnimationFrame(tick)
  }, [draw])
  useEffect(() => { raf.current = requestAnimationFrame(tick); return () => { if (raf.current) cancelAnimationFrame(raf.current) } }, [tick])

  // độ lệch pha chuẩn hoá về (−π, π]
  let dphi = (phi2 ?? 0) - (phi1 ?? 0)
  dphi = Math.atan2(Math.sin(dphi), Math.cos(dphi))
  const ad = Math.abs(dphi)
  const rel = ad < 0.04 ? 'cùng pha' : Math.abs(ad - Math.PI) < 0.04 ? 'ngược pha' : Math.abs(ad - Math.PI / 2) < 0.04 ? 'vuông pha' : (dphi > 0 ? 'dao động 2 sớm pha hơn' : 'dao động 2 trễ pha hơn')

  const inputErr = phi1 === null || phi2 === null

  return (
    <div className="rounded-2xl border border-indigo-200 bg-white p-4 my-3">
      <h4 className="font-bold text-indigo-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H} className="w-full rounded-lg border border-gray-100 bg-slate-50" />
      <div className="flex items-center justify-center gap-3 my-2 text-sm">
        <span className="text-gray-600">Độ lệch pha Δφ = φ₂ − φ₁ =</span>
        <b className="text-indigo-700">{phi1 !== null && phi2 !== null ? `${dphi.toFixed(2)} rad (${fmtPi(dphi)})` : '—'}</b>
        <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-bold">{phi1 !== null && phi2 !== null ? rel : 'pha không hợp lệ'}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mt-2 text-sm">
        <label className="flex items-center gap-2"><span className="w-24 text-blue-700 font-semibold">A₁</span><input type="range" min={0.2} max={1.4} step={0.1} value={a1} onChange={(e) => { touch(); setA1(Number(e.target.value)) }} className="flex-1 accent-blue-500" /><span className="w-10 text-right font-semibold">{a1.toFixed(1)}</span></label>
        <label className="flex items-center gap-2"><span className="w-24 text-red-700 font-semibold">A₂</span><input type="range" min={0.2} max={1.4} step={0.1} value={a2} onChange={(e) => { touch(); setA2(Number(e.target.value)) }} className="flex-1 accent-red-500" /><span className="w-10 text-right font-semibold">{a2.toFixed(1)}</span></label>
        <label className="flex items-center gap-2"><span className="w-24 text-blue-700 font-semibold">φ₁ (rad)</span><input type="text" value={p1s} onChange={(e) => { touch(); setP1s(e.target.value) }} placeholder="vd: 0" className={`flex-1 px-2 py-1 rounded-lg border bg-white ${phi1 === null ? 'border-red-400' : 'border-gray-200'}`} /></label>
        <label className="flex items-center gap-2"><span className="w-24 text-red-700 font-semibold">φ₂ (rad)</span><input type="text" value={p2s} onChange={(e) => { touch(); setP2s(e.target.value) }} placeholder="vd: pi/2" className={`flex-1 px-2 py-1 rounded-lg border bg-white ${phi2 === null ? 'border-red-400' : 'border-gray-200'}`} /></label>
      </div>
      {inputErr && <p className="text-[11px] text-red-500 mt-1">Pha không hợp lệ — nhập số hoặc biểu thức radian chứa pi, vd: pi/2, -pi/3, 2*pi/3.</p>}
      <div className="flex gap-2 mt-3">
        <button onClick={() => { touch(); setRunning((r) => !r) }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white font-bold hover:bg-indigo-700">{running ? <><Pause className="w-4 h-4" /> Tạm dừng</> : <><Play className="w-4 h-4" /> Chạy</>}</button>
        <button onClick={() => { tRef.current = 0 }} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50">Về t = 0</button>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Cùng pha: Δφ = 0; ngược pha: Δφ = ±π; vuông pha: Δφ = ±π/2. Hai vật bên trái dao động theo thời gian thực; con trỏ trên đồ thị cho thấy độ lệch ngang giữa hai đường = độ lệch pha.</p>
    </div>
  )
}
export default TwoOscillationsSimulation
